import {Controller} from "stimulus"
import {openPdfFrame, closePdfFrame} from "story/components/fwa/utils/pdf-frame"
import {mountOverlay} from "story/components/fwa/utils/pdf-loading-overlay"

/**
 * Stimulus controller for the FWA Dashboard "Download Assessment" button.
 *
 * Opens a hidden same-origin iframe at /wellness/fwa/pdf-frame whose
 * bundle exposes window.BanzaiPdfGen.generate(data). The iframe renders
 * the PDF in its own document (matching the FWA Coach's CSS environment)
 * and returns the result as a {blob, filename}. The parent dashboard
 * then triggers the actual file download — this is required because
 * Chrome blocks programmatic downloads initiated by hidden cross-doc
 * iframes.
 *
 * The loading overlay is mounted in the DASHBOARD document (not the
 * iframe) so the user gets visible feedback during the multi-second
 * render.
 *
 * Usage in Hiccup:
 *   [:button {:data-controller "fwa-download"
 *             :data-fwa-download-run-uuid-value run-uuid
 *             :data-action "click->fwa-download#download"}
 *    icon]
 */
export default class extends Controller {
  static get values() {
    return {runUuid: String}
  }

  async download(event) {
    event.preventDefault()
    event.stopPropagation()

    // Stimulus 1.x doesn't support the values API; fall back to reading the
    // attribute directly. (See fwa_debug_profile_controller.js for the same workaround.)
    const runUuid = this.runUuidValue || this.element.getAttribute("data-fwa-download-run-uuid-value")
    if (!runUuid) {
      alert("Unable to download — no assessment selected.")
      return
    }

    const btn = this.element
    btn.disabled = true
    btn.style.opacity = "0.5"

    const overlay = mountOverlay()
    let iframe = null
    try {
      const res = await fetch(`/api4/wellness/fwa/pdf-data?run-uuid=${encodeURIComponent(runUuid)}`)
      const json = await res.json()
      if (!res.ok || !json.success || !json.data) {
        alert(json.error || "Could not load assessment data.")
        return
      }
      iframe = await openPdfFrame()
      const {blob, filename} = await iframe.contentWindow.BanzaiPdfGen.generate(json.data)
      triggerDownload(blob, filename)
    } catch (err) {
      console.error("FWA dashboard download failed:", err)
      alert(`Failed to download assessment results: ${err.message || err}`)
    } finally {
      if (iframe) closePdfFrame(iframe)
      overlay.unmount()
      btn.disabled = false
      btn.style.opacity = ""
    }
  }
}

// Trigger a file download in the parent document using a Blob produced
// by the iframe. Doing this from the parent (not the iframe) gives the
// click a fresh user-gesture pedigree, which modern Chrome requires for
// downloads to actually fire.
const triggerDownload = (blob, filename) => {
  const url = URL.createObjectURL(blob)
  const a = document.createElement("a")
  a.href = url
  a.download = filename
  a.style.display = "none"
  document.body.appendChild(a)
  a.click()
  a.remove()
  // Revoke after a tick so the browser has a chance to start the download.
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}
